// © 2024–2026 Jos Jozef Sytze van der Werf — Alle rechten voorbehouden.
import React from 'react'
import { useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card }      from '@/components/ui/Card'
import { Textarea }  from '@/components/ui/Textarea'
import { Input }     from '@/components/ui/Input'
import { PlaceField } from '@/components/ui/PlaceField'
import { RadioGroup } from '@/components/ui/RadioGroup'
import { InfoButton } from '@/components/ui/InfoButton'
import { WizardStepShell } from './WizardStepShell'
import { ROUTES } from '@/lib/constants/routes'
import { loadFuneralWishes, saveFuneralWishes, EMPTY_FUNERAL, type FuneralWishesData } from '@/services/wishes'
import type { SaveStatus } from '@/hooks/useAutosave'

const TYPE_WITH_INFO = [
  { value: 'burial',    label: 'Begraving',      description: 'Begraven op een begraafplaats' },
  { value: 'cremation', label: 'Crematie',        description: 'Verbranding - as kan worden bijgezet, bewaard of uitgestrooid' },
  { value: 'natural',   label: 'Natuurbegraving', description: 'Begraving in een natuurgebied zonder traditioneel grafmonument' },
  { value: 'open',      label: 'Geen voorkeur',   description: 'Laat dit over aan mijn naasten' },
]
const CEREMONY = [
  { value: 'religious', label: 'Religieuze dienst' },
  { value: 'civil',     label: 'Burgerlijke / niet-religieuze plechtigheid' },
  { value: 'private',   label: 'Alleen voor familie en directe vrienden' },
  { value: 'none',      label: 'Geen officiële ceremonie' },
]
const LOCATION_PREF = [
  { value: 'nl',      label: 'Nederland' },
  { value: 'origin',  label: 'Land van herkomst' },
  { value: 'other',   label: 'Ander land' },
  { value: 'none',    label: 'Geen voorkeur' },
]


// V37: Compacte koppelkaart - geen dubbele invoervelden
function UitvaartVerzekeringCard({ saveNow }: { saveNow: () => Promise<void> }) {
  const navigate = useNavigate()
  return (
    <div className="rounded-2xl bg-sage-50 border border-sage-200 px-5 py-4 flex flex-col gap-3">
      <div className="flex items-start gap-3">
        <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-moss flex-shrink-0 mt-0.5">
          <span className="text-white text-sm font-bold">€</span>
        </div>
        <div className="flex-1">
          <p className="text-sm font-semibold text-sage-900 mb-1">Uitvaartverzekering</p>
          <p className="text-xs text-sage-700 leading-relaxed">
            Leg hier vast of u een uitvaartverzekering heeft, bij welke verzekeraar
            en wat het polisnummer is. Handig voor uw nabestaanden.
          </p>
        </div>
      </div>
      <div className="border-t border-sage-200 pt-3 flex flex-col gap-2">
        <button
          type="button"
          onClick={async () => { await saveNow(); navigate(ROUTES.WISHES_FUNERAL_COSTS) }}
          className="flex items-center justify-between rounded-xl border border-sage-300 bg-white px-4 py-2.5 text-sm font-medium text-sage-700 hover:bg-sage-50 hover:border-sage-400 transition-colors"
        >
          <span>Uitvaartverzekering &amp; kosten invullen</span>
          <span className="text-sage-500">→</span>
        </button>
        <p className="text-xs text-sage-600 leading-relaxed">
          WilsApp legt uw persoonlijke wensen vast. Uw officiële polis staat bij uw verzekeraar.
        </p>
      </div>
    </div>
  )
}

export function Step11FuneralWishes({ onSaveStatus, saveRef }: { onSaveStatus: (s: SaveStatus) => void; saveRef?: React.MutableRefObject<(() => Promise<void>) | null> }) {
  const load = useCallback(loadFuneralWishes, [])
  const save = useCallback(saveFuneralWishes, [])
  return (
    <WizardStepShell emptyData={EMPTY_FUNERAL} load={load} save={save} onSaveStatus={onSaveStatus} saveRef={saveRef}>
      {(data, upd, saveNow) => (
        <div className="flex flex-col gap-5">
          <div><h2 className="text-xl font-semibold text-ink mb-1">Uitvaartwensen</h2><p className="text-sm text-ink-3">Leg vast hoe u wilt worden herdacht en begraven.</p></div>

          {/* Type uitvaart */}
          <Card>
            <div className="flex items-center gap-2 mb-4">
              <h3 className="text-base font-semibold text-ink">Begraven of cremeren</h3>
              <InfoButton title="Soorten uitvaart"
                content={<>
                  <p><strong>Begraving:</strong> begraven op een begraafplaats. Het lichaam wordt in een kist begraven.</p>
                  <p><strong>Crematie:</strong> verbranding van het lichaam. De as kan worden bijgezet in een urn, bewaard of uitgestrooid op een speciale locatie.</p>
                  <p><strong>Natuurbegraving:</strong> begraving in een aangewezen natuurgebied, zonder traditioneel grafmonument. Het graf gaat op in de natuur.</p>
                </>} />
            </div>
            <RadioGroup name="type" options={TYPE_WITH_INFO} value={data.type} onChange={v => upd('type', v as FuneralWishesData['type'])} />
          </Card>

          {/* Locatie uitvaart */}
          <Card>
            <div className="flex items-center gap-2 mb-4">
              <h3 className="text-base font-semibold text-ink">Locatie van de uitvaart</h3>
              <InfoButton title="Locatie van uitvaart"
                content={<>
                  <p><strong>Land van herkomst of buitenland:</strong> een uitvaart of begraving in het buitenland vraagt extra voorbereiding.</p>
                  <p>Denk aan: repatriëring van het lichaam, internationale documenten, lokale regelgeving, extra kosten en een lokale uitvaartverzorger.</p>
                  <p>Leg een lokale contactpersoon vast als u dit wenst.</p>
                </>} />
            </div>
            <RadioGroup name="locationPref" options={LOCATION_PREF}
              value={(data as FuneralWishesData & { locationPref?: string }).locationPref ?? ''}
              onChange={v => upd('locationPref' as keyof FuneralWishesData, v as string)} />
            {((data as FuneralWishesData & { locationPref?: string }).locationPref === 'origin' ||
              (data as FuneralWishesData & { locationPref?: string }).locationPref === 'other') && (
              <div className="mt-4 flex flex-col gap-3 animate-slide-down">
                <Input label="Land" value={(data as FuneralWishesData & { locationCountry?: string }).locationCountry ?? ''}
                  onChange={e => upd('locationCountry' as keyof FuneralWishesData, e.target.value as string)}
                  placeholder="Bijv: Marokko, Turkije, Suriname..." />
                <Input label="Plaats of regio" value={data.locationWish} onChange={e => upd('locationWish', e.target.value as FuneralWishesData['locationWish'])} placeholder="Bijv: Casablanca, Istanbul..." />
                <Textarea label="Aanvullende wensen voor uitvaart in het buitenland"
                  value={(data as FuneralWishesData & { locationNotes?: string }).locationNotes ?? ''}
                  onChange={e => upd('locationNotes' as keyof FuneralWishesData, e.target.value as string)}
                  placeholder="Bijv: contactpersoon ter plaatse, religieuze wensen, repatriëring..." maxLength={400} />
              </div>
            )}
            {(data as FuneralWishesData & { locationPref?: string }).locationPref === 'nl' && (
              <div className="mt-4 animate-slide-down">
                <Input label="Voorkeurlocatie of begraafplaats" value={data.locationWish}
                  onChange={e => upd('locationWish', e.target.value as FuneralWishesData['locationWish'])}
                  placeholder="Bijv: Begraafplaats in mijn geboorteplaats, crematorium X..." />
              </div>
            )}
          </Card>

          {/* Plechtigheid */}
          <Card>
            <h3 className="text-base font-semibold text-ink mb-4">Soort plechtigheid</h3>
            <RadioGroup name="ceremony" options={CEREMONY} value={data.ceremonyType} onChange={v => upd('ceremonyType', v as FuneralWishesData['ceremonyType'])} />
          </Card>

          {/* Uitvaartverzorger */}
          <Card>
            <h3 className="text-sm font-semibold text-ink mb-3">Uitvaartverzorger</h3>
            <div className="flex flex-col gap-3">
              <PlaceField
                label="Plaats"
                value={data.funeralHomeCity}
                onChange={v => upd('funeralHomeCity', v as FuneralWishesData['funeralHomeCity'])}
                placeholder="Typ een plaats..."
                hint="Vaak herinnert men zich de plaats eerder dan de exacte naam."
              />
              <Input
                label="Naam uitvaartverzorger of -ondernemer"
                value={data.funeralHome}
                onChange={e => upd('funeralHome', e.target.value as FuneralWishesData['funeralHome'])}
                placeholder="Bijv: Uitvaartcentrum De Roos"
              />
              <Input
                label="Contactpersoon (optioneel)"
                value={data.funeralHomeContact}
                onChange={e => upd('funeralHomeContact', e.target.value as FuneralWishesData['funeralHomeContact'])}
                placeholder="Bijv: mevrouw Koster"
                hint="Naam van de persoon die u wenst voor de uitvaartbegeleiding."
              />
              <Input
                label="Telefoonnummer (optioneel)"
                type="tel"
                value={data.funeralHomePhone}
                onChange={e => upd('funeralHomePhone', e.target.value as FuneralWishesData['funeralHomePhone'])}
                placeholder="Bijv: 072 123 45 67"
              />
              <Textarea
                label="Toelichting (optioneel)"
                value={data.funeralHomeNotes}
                onChange={e => upd('funeralHomeNotes', e.target.value as FuneralWishesData['funeralHomeNotes'])}
                placeholder="Bijv: zij kent onze familie al jaren en weet wat wij belangrijk vinden"
                maxLength={300}
              />
            </div>
          </Card>

          {/* Muziek & Bloemen */}
          <Card>
            <h3 className="text-base font-semibold text-ink mb-4">Muziek, bloemen &amp; kleding</h3>
            <div className="flex flex-col gap-3">
              <Textarea label="Muziekwensen" value={data.music} onChange={e => upd('music', e.target.value as FuneralWishesData['music'])} placeholder="Bijv: Bach cello suite nr. 1, Fields of Gold van Sting..." maxLength={300} />
              <div className="grid gap-3 sm:grid-cols-2">
                <Input label="Bloemenwensen" value={data.flowers} onChange={e => upd('flowers', e.target.value as FuneralWishesData['flowers'])} placeholder="Witte rozen, geen plastic" />
                <Input label="Kledingwensen" value={data.clothing} onChange={e => upd('clothing', e.target.value as FuneralWishesData['clothing'])} placeholder="Mijn blauwe kostuum" />
              </div>
            </div>
          </Card>

          {/* Rouwkaart & uitnodigingen */}
          <Card>
            <h3 className="text-base font-semibold text-ink mb-4">Rouwkaart &amp; uitnodigingen</h3>
            <div className="flex flex-col gap-3">
              <Textarea label="Rouwkaarttekst" value={data.obituaryText} onChange={e => upd('obituaryText', e.target.value as FuneralWishesData['obituaryText'])} placeholder="Na een leven vol liefde voor zijn/haar familie en muziek..." maxLength={500} showCount />
              <Textarea label="Wie uitnodigen" value={data.notificationList} onChange={e => upd('notificationList', e.target.value as FuneralWishesData['notificationList'])} placeholder="Bijv: werkgever, buren, koor, bridgeclubs..." maxLength={400} />
              <Textarea label="Rituelen, geloofsovertuiging, persoonlijke wensen" value={data.specialRequests} onChange={e => upd('specialRequests', e.target.value as FuneralWishesData['specialRequests'])} placeholder="Bijv: kerkdienst, islamitische begrafenis, geen religieuze elementen..." maxLength={400} showCount />
            </div>
          </Card>

          {/* V37: Uitvaartverzekering - compact koppelkaart, geen dubbele invoer */}
          <UitvaartVerzekeringCard saveNow={saveNow} />
        </div>
      )}
    </WizardStepShell>
  )
}
